/* eslint-disable react/prop-types */
/**
 * RestroPulse POS · mock data (Sprint 2 · restructured).
 *
 * Part 6.1 · Every sellable thing is its OWN menu item row. No item
 * carries variants or add-ons — modifiers are separate menu items
 * with distinct prices. Nothing may be sold that does not exist as
 * a menu item row.
 *
 * TAX MODEL:
 *   • Food items ................. 5% GST → half CGST + half SGST
 *   • Liquor items ............... 20% VAT, no GST
 *   • Beverage (non-liquor) ...... 5% GST
 *
 * ROUTING MODEL (Part 7):
 *   • routing="kitchen" ........... food
 *   • routing="bar" ............... beverage + liquor
 */

// ─── Categories ───────────────────────────────────────────────────
export const CATEGORIES = [
  { id: "cat_starters",   name: "Starters",       icon: "🍢" },
  { id: "cat_mains",      name: "Main Course",    icon: "🍛" },
  { id: "cat_indian_br",  name: "Indian Breads",  icon: "🫓" },
  { id: "cat_rice",       name: "Rice & Biryani", icon: "🍚" },
  { id: "cat_chinese",    name: "Chinese",        icon: "🥡" },
  { id: "cat_desserts",   name: "Desserts",       icon: "🍰" },
  { id: "cat_beverages",  name: "Beverages",      icon: "🥤" },
  { id: "cat_liquor",     name: "Liquor",         icon: "🍺" },
];

// Helper — routing derived from category. Bar carries beverages and liquor.
const routingFor = (cat) => (cat === "cat_liquor" || cat === "cat_beverages") ? "bar" : "kitchen";

const item = (id, name, price, category_id) => ({
  id, name, price, category_id,
  tax_class: category_id === "cat_liquor" ? "liquor" : "food",
  routing: routingFor(category_id),
});

// ─── Items — every sellable line is its own row ─────────────────────
export const ITEMS = [
  // ── Starters ──
  item("i_paneer_tikka",         "Paneer Tikka",                320, "cat_starters"),
  item("i_paneer_tikka_cheese",  "Paneer Tikka · Extra Cheese", 360, "cat_starters"),
  item("i_chicken_65_half",      "Chicken 65 · Half",           280, "cat_starters"),
  item("i_chicken_65_full",      "Chicken 65 · Full",           380, "cat_starters"),
  item("i_veg_manch_dry",        "Veg Manchurian · Dry",        260, "cat_starters"),
  item("i_veg_manch_gravy",      "Veg Manchurian · Gravy",      280, "cat_starters"),
  item("i_chilli_paneer",        "Chilli Paneer",               340, "cat_starters"),
  item("i_hara_bhara",           "Hara Bhara Kabab",            240, "cat_starters"),
  item("i_tandoori_ck_qtr",      "Tandoori Chicken · Quarter",  340, "cat_starters"),
  item("i_tandoori_ck_half",     "Tandoori Chicken · Half",     440, "cat_starters"),
  item("i_tandoori_ck_full",     "Tandoori Chicken · Full",     520, "cat_starters"),
  item("i_seekh_kabab",          "Mutton Seekh Kabab",          460, "cat_starters"),
  item("i_prawn_koliwada",       "Prawn Koliwada",              540, "cat_starters"),
  item("i_paneer_65",            "Paneer 65",                   320, "cat_starters"),
  item("i_gobi_65",              "Gobi 65",                     240, "cat_starters"),
  item("i_chicken_lollipop",     "Chicken Lollipop",            380, "cat_starters"),
  item("i_fish_amritsari",       "Fish Amritsari",              460, "cat_starters"),

  // ── Main Course ──
  item("i_paneer_butter",        "Paneer Butter Masala",        340, "cat_mains"),
  item("i_dal_makhani",          "Dal Makhani",                 280, "cat_mains"),
  item("i_kadai_paneer",         "Kadai Paneer",                320, "cat_mains"),
  item("i_butter_chicken",       "Butter Chicken",              420, "cat_mains"),
  item("i_butter_chicken_extra", "Butter Chicken · Extra Gravy",480, "cat_mains"),
  item("i_chicken_curry",        "Chicken Curry",               380, "cat_mains"),
  item("i_mutton_rogan",         "Mutton Rogan Josh",           540, "cat_mains"),
  item("i_dal_tadka",            "Dal Tadka",                   220, "cat_mains"),
  item("i_veg_kolhapuri",        "Veg Kolhapuri",               300, "cat_mains"),
  item("i_malai_kofta",          "Malai Kofta",                 320, "cat_mains"),
  item("i_egg_curry",            "Egg Curry",                   260, "cat_mains"),
  item("i_prawn_curry",          "Prawn Curry",                 520, "cat_mains"),
  item("i_fish_curry",           "Fish Curry",                  460, "cat_mains"),

  // ── Indian Breads ──
  item("i_butter_naan",          "Butter Naan",                  60, "cat_indian_br"),
  item("i_garlic_naan",          "Garlic Naan",                  80, "cat_indian_br"),
  item("i_tandoori_roti",        "Tandoori Roti",                30, "cat_indian_br"),
  item("i_tandoori_roti_butter", "Tandoori Roti · With Butter",  40, "cat_indian_br"),
  item("i_laccha_paratha",       "Laccha Paratha",               70, "cat_indian_br"),
  item("i_kulcha_paneer",        "Stuffed Kulcha · Paneer",      90, "cat_indian_br"),
  item("i_kulcha_onion",         "Stuffed Kulcha · Onion",       70, "cat_indian_br"),
  item("i_kulcha_aloo",          "Stuffed Kulcha · Aloo",        70, "cat_indian_br"),
  item("i_missi_roti",           "Missi Roti",                   40, "cat_indian_br"),
  item("i_bhatura",              "Bhatura",                      50, "cat_indian_br"),
  item("i_naan_plain",           "Plain Naan",                   50, "cat_indian_br"),

  // ── Rice & Biryani ──
  item("i_veg_biryani",          "Veg Biryani",                 260, "cat_rice"),
  item("i_chicken_biryani",      "Chicken Biryani · Full",      340, "cat_rice"),
  item("i_chicken_biryani_half", "Chicken Biryani · Half",      260, "cat_rice"),
  item("i_mutton_biryani",       "Mutton Biryani",              460, "cat_rice"),
  item("i_hyderabadi_bir",       "Hyderabadi Biryani",          480, "cat_rice"),
  item("i_jeera_rice",           "Jeera Rice",                  180, "cat_rice"),
  item("i_veg_pulao",            "Veg Pulao",                   220, "cat_rice"),
  item("i_curd_rice",            "Curd Rice",                   160, "cat_rice"),
  item("i_dal_khichdi",          "Dal Khichdi",                 200, "cat_rice"),
  item("i_prawn_biryani",        "Prawn Biryani",               480, "cat_rice"),
  item("i_egg_biryani",          "Egg Biryani",                 280, "cat_rice"),
  item("i_plain_rice",           "Plain Steamed Rice",          140, "cat_rice"),

  // ── Chinese ──
  item("i_noodles_hakka",        "Veg Hakka Noodles",           220, "cat_chinese"),
  item("i_noodles_schezwan",     "Veg Schezwan Noodles",        240, "cat_chinese"),
  item("i_noodles_singapore",    "Veg Singapore Noodles",       240, "cat_chinese"),
  item("i_ck_noodles",           "Chicken Noodles",             280, "cat_chinese"),
  item("i_veg_fried_rice",       "Veg Fried Rice",              220, "cat_chinese"),
  item("i_ck_fried_rice",        "Chicken Fried Rice",          280, "cat_chinese"),
  item("i_veg_manch_soup",       "Veg Manchow Soup",            160, "cat_chinese"),
  item("i_sweet_corn_soup",      "Sweet Corn Soup",             140, "cat_chinese"),
  item("i_hot_sour_soup",        "Hot & Sour Soup",             140, "cat_chinese"),
  item("i_veg_spring",           "Veg Spring Roll",             220, "cat_chinese"),
  item("i_paneer_chilli",        "Paneer Chilli",               320, "cat_chinese"),
  item("i_gobi_manch",           "Gobi Manchurian",             240, "cat_chinese"),

  // ── Desserts ──
  item("i_gulab_jamun",          "Gulab Jamun (2 pc)",          120, "cat_desserts"),
  item("i_gajar_halwa",          "Gajar Halwa",                 160, "cat_desserts"),
  item("i_kulfi_malai",          "Kulfi Falooda · Malai",       140, "cat_desserts"),
  item("i_kulfi_pista",          "Kulfi Falooda · Pista",       160, "cat_desserts"),
  item("i_kulfi_mango",          "Kulfi Falooda · Mango",       160, "cat_desserts"),
  item("i_ice_cream",            "Ice Cream Scoop",              80, "cat_desserts"),
  item("i_rasmalai",             "Rasmalai (2 pc)",             140, "cat_desserts"),
  item("i_brownie",              "Chocolate Brownie",           180, "cat_desserts"),
  item("i_jalebi",               "Jalebi",                      100, "cat_desserts"),

  // ── Beverages (routing = bar) ──
  item("i_masala_chai",          "Masala Chai",                  40, "cat_beverages"),
  item("i_cutting_chai",         "Cutting Chai",                 30, "cat_beverages"),
  item("i_coffee",               "Filter Coffee",                60, "cat_beverages"),
  item("i_cold_coffee",          "Cold Coffee",                 140, "cat_beverages"),
  item("i_lime_soda_sweet",      "Fresh Lime Soda · Sweet",      80, "cat_beverages"),
  item("i_lime_soda_salt",       "Fresh Lime Soda · Salt",       80, "cat_beverages"),
  item("i_lime_soda_mixed",      "Fresh Lime Soda · Mixed",      80, "cat_beverages"),
  item("i_lassi",                "Sweet Lassi",                 100, "cat_beverages"),
  item("i_mango_lassi",          "Mango Lassi",                 140, "cat_beverages"),
  item("i_water_1l",             "Bisleri 1L",                   60, "cat_beverages"),
  item("i_pepsi",                "Pepsi",                        80, "cat_beverages"),
  item("i_coke",                 "Coca-Cola",                    80, "cat_beverages"),
  item("i_thums_up",             "Thums Up",                     80, "cat_beverages"),

  // ── LIQUOR (20% VAT, routing = bar) ──
  item("i_kingfisher",           "Kingfisher (650ml)",          340, "cat_liquor"),
  item("i_bud_pint",             "Budweiser (330ml)",           240, "cat_liquor"),
  item("i_corona",               "Corona (330ml)",              380, "cat_liquor"),
  item("i_heineken",             "Heineken (330ml)",            360, "cat_liquor"),
  item("i_pina_colada",          "Piña Colada",                 420, "cat_liquor"),
  item("i_mojito",               "Classic Mojito",              380, "cat_liquor"),
  item("i_long_island",          "Long Island Iced Tea",        480, "cat_liquor"),
  item("i_gin_tonic",            "Gin & Tonic",                 360, "cat_liquor"),
  item("i_whiskey_peg",          "Whiskey Regular (30ml)",      280, "cat_liquor"),
  item("i_whiskey_prem",         "Whiskey Premium (30ml)",      400, "cat_liquor"),
  item("i_whiskey_import",       "Whiskey Imported (30ml)",     620, "cat_liquor"),
  item("i_vodka_peg",            "Vodka (30ml)",                260, "cat_liquor"),
  item("i_rum_peg",              "Rum (30ml)",                  220, "cat_liquor"),
  item("i_red_wine",             "Red Wine (Glass)",            380, "cat_liquor"),
  item("i_white_wine",           "White Wine (Glass)",          360, "cat_liquor"),
  item("i_beer_pitcher",         "Beer Pitcher (3L)",           980, "cat_liquor"),
  item("i_sangria",              "Sangria",                     440, "cat_liquor"),
];

// ─── Tables ────────────────────────────────────────────────────────
// No `status` field — table state is DERIVED per Contract §5 from the
// orders in state. See `tableState()` in posReducer.js.
export const TABLES = [
  // Ground floor · 12 tables
  { id: "t_g01", label: "T1",  zone: "ground",  capacity: 2 },
  { id: "t_g02", label: "T2",  zone: "ground",  capacity: 4 },
  { id: "t_g03", label: "T3",  zone: "ground",  capacity: 4 },
  { id: "t_g04", label: "T4",  zone: "ground",  capacity: 2 },
  { id: "t_g05", label: "T5",  zone: "ground",  capacity: 6 },
  { id: "t_g06", label: "T6",  zone: "ground",  capacity: 4 },
  { id: "t_g07", label: "T7",  zone: "ground",  capacity: 2 },
  { id: "t_g08", label: "T8",  zone: "ground",  capacity: 4 },
  { id: "t_g09", label: "T9",  zone: "ground",  capacity: 4 },
  { id: "t_g10", label: "T10", zone: "ground",  capacity: 6 },
  { id: "t_g11", label: "T11", zone: "ground",  capacity: 2 },
  { id: "t_g12", label: "T12", zone: "ground",  capacity: 8 },
  // Terrace · 10 tables
  { id: "t_r01", label: "R1",  zone: "terrace", capacity: 4 },
  { id: "t_r02", label: "R2",  zone: "terrace", capacity: 6 },
  { id: "t_r03", label: "R3",  zone: "terrace", capacity: 2 },
  { id: "t_r04", label: "R4",  zone: "terrace", capacity: 8 },
  { id: "t_r05", label: "R5",  zone: "terrace", capacity: 4 },
  { id: "t_r06", label: "R6",  zone: "terrace", capacity: 2 },
  { id: "t_r07", label: "R7",  zone: "terrace", capacity: 4 },
  { id: "t_r08", label: "R8",  zone: "terrace", capacity: 6 },
  { id: "t_r09", label: "R9",  zone: "terrace", capacity: 4 },
  { id: "t_r10", label: "R10", zone: "terrace", capacity: 2 },
  // Private dining · 6 tables
  { id: "t_p01", label: "P1",  zone: "private", capacity: 6 },
  { id: "t_p02", label: "P2",  zone: "private", capacity: 8 },
  { id: "t_p03", label: "P3",  zone: "private", capacity: 4 },
  { id: "t_p04", label: "P4",  zone: "private", capacity: 6 },
  { id: "t_p05", label: "P5",  zone: "private", capacity: 10 },
  { id: "t_p06", label: "P6",  zone: "private", capacity: 4 },
];

// Zones surfaced in the floor-view zone strip. Order preserved.
export const ZONES = [
  { id: "ground",  label: "Ground floor" },
  { id: "terrace", label: "Terrace"      },
  { id: "private", label: "Private"      },
];

// Kitchen-note quick presets — Task 1c. Additive & toggleable.
export const NOTE_PRESETS = [
  "Less salt", "Less spice", "Less sugar",
  "No onion",  "No garlic",  "Jain", "Extra spicy",
];

// ─── Stewards ─────────────────────────────────────────────────────
export const STEWARDS = [
  { id: "s_1", name: "Priya" },
  { id: "s_2", name: "Ravi"  },
  { id: "s_3", name: "Karan" },
  { id: "s_4", name: "Meena" },
];

// ─── Sprint 7 correction 3 · Mock staff records ───────────────────
// Six staff, each with a role and a 4-digit staff PIN. The staff
// login screen validates against this list; a manager-role PIN is
// what unlocks gated actions (voids, comps, reopen). Cashiers cannot
// authorise their own voids.
//
// This is mock data — production reads from the outlet's staff
// directory, PINs are hashed, and the manager-role check happens
// server-side. The DEMO_PIN below is deliberately exposed on the
// /pos-demo lock screen; do NOT surface it on the authenticated
// terminal.
export const STAFF = [
  { id: "u_meera",  name: "Meera Kapoor",  role: "cashier", pin: "2201", active: true },
  { id: "u_arjun",  name: "Arjun Nair",    role: "cashier", pin: "2202", active: true },
  { id: "u_priya",  name: "Priya Sharma",  role: "steward", pin: "2203", active: true },
  { id: "u_ravi",   name: "Ravi Menon",    role: "steward", pin: "2204", active: true },
  { id: "u_leela",  name: "Leela Iyer",    role: "manager", pin: "9911", active: true },
  { id: "u_vikram", name: "Vikram Rao",    role: "manager", pin: "9922", active: true },
];
export const DEMO_STAFF_ID = "u_meera";
export const DEMO_STAFF_PIN = "2201";
// Sprint 3B correction · idle-lock default is 30 MINUTES.
// The previous value of 120 seconds was unusable — stewards took a
// phone call and had to re-punch a PIN mid-order. The correct
// production default is 30 minutes; tenants override via
// PosSettings.js → PosSecuritySettingsSection (stored on the outlet
// record as pos_settings.idle_lock_seconds).
export const IDLE_LOCK_SECONDS = 1800;

// ─── Mock guest ledger · Part 5 ─────────────────────────────────────
// Phone-first lookup for returning guests. In production this is a
// backend call to the outlet's guest database; the prototype just
// resolves against a small in-memory map. Everything not listed here
// is treated as a new guest.
export const MOCK_GUESTS = {
  "9820011122": {
    name: "Anita Krishnamurthy",
    addresses: [
      { id: "adr_1", label: "Home", line: "1204 Rustomjee, Andheri West, Mumbai 400058" },
      { id: "adr_2", label: "Office", line: "Building C, Godrej BKC, Mumbai 400051" },
    ],
  },
  "9811223344": {
    name: "Vikram Nair",
    addresses: [
      { id: "adr_1", label: "Home", line: "42/3 Vaikom Villa, Fort Kochi 682001" },
    ],
  },
  "9900233112": {
    name: "Deepa Sharma",
    addresses: [
      { id: "adr_1", label: "Home", line: "Flat 803, Prestige Falcon, Koramangala, Bengaluru 560095" },
      { id: "adr_2", label: "Sister's place", line: "MIG-45, HAL 3rd Stage, Bengaluru 560075" },
      { id: "adr_3", label: "Office", line: "Level 6, Prestige Trade Tower, Bengaluru 560025" },
    ],
  },
};

// ─── Mock aggregator inbound queue · Part 6 ─────────────────────────
// The connector service is out of scope for the terminal. The tray
// here consumes a static list; a real deployment would receive these
// via a push channel and populate the same shape.

/** Case-insensitive phone lookup returning name + addresses, or null. */
export const lookupGuestByPhone = (phone) => {
  const key = String(phone || "").replace(/[^0-9]/g, "");
  return MOCK_GUESTS[key] || null;
};

export const MOCK_AGGREGATOR_ORDERS = [
  {
    id: "agg_sw_84391",
    platform: "swiggy",
    platform_ref: "SW-9438712",
    received_at: Date.now() - 45_000,
    guest: { name: "Aditya Rane", phone: "9820011122", address: "1204 Rustomjee, Andheri West, Mumbai 400058" },
    items: [
      { item_id: "i_paneer_tikka",     qty: 2 },
      { item_id: "i_naan",             qty: 4 },
      { item_id: "i_masala_chai",      qty: 2 },
    ],
    total_hint: 780,
  },
  {
    id: "agg_zo_11024",
    platform: "zomato",
    platform_ref: "ZO-8829145",
    received_at: Date.now() - 400_000,          // Part 6 escalation · older than threshold
    guest: { name: "Radhika S", phone: "9900233112", address: "Flat 803, Prestige Falcon, Koramangala, Bengaluru 560095" },
    items: [
      { item_id: "i_chicken_biryani",  qty: 1 },
      { item_id: "i_gulab_jamun",      qty: 2 },
      { item_id: "i_kingfisher",       qty: 2 },
    ],
    total_hint: 1450,
  },
  {
    id: "agg_sw_84515",
    platform: "swiggy",
    platform_ref: "SW-9439018",
    received_at: Date.now() - 12_000,
    guest: { name: "Karthik", phone: "9811223344", address: "42/3 Vaikom Villa, Fort Kochi 682001" },
    items: [
      { item_id: "i_veg_fried_rice",   qty: 1 },
      { item_id: "i_manchurian",       qty: 1 },
    ],
    total_hint: 520,
  },
];
// Part 6 · escalate if not accepted within this many milliseconds.
export const AGGREGATOR_ESCALATION_MS = 300_000; // 5 minutes

// ─── Tax config (used by reducer) ─────────────────────────────────
export const TAX_CONFIG = {
  food:   { cgst: 0.025, sgst: 0.025, vat: 0 },     // 5% split
  liquor: { cgst: 0,     sgst: 0,     vat: 0.20 },  // 20% VAT
};

// Sprint 6 correction 1 · Section 170 of the CGST Act with Rule 51
// rounds every tax component to the nearest rupee — 50 paise or more
// rounds up, below 50 paise is ignored. There is NO "round to the
// nearest 50 paise" step; 50p is the threshold, not the increment.
//
// `tax_rounding_mode` is a tenant-configurable knob. Production
// deployments pass this value per tenant (some accountants prefer
// exact paise on tax lines and round only at the total).
//   "per_component" — CGST, SGST, UTGST, IGST, cess and VAT each
//                     round on their own; round_off reconciles the
//                     summed components to the payable amount.
//   "total_only"    — tax lines keep exact paise; only the invoice
//                     total rounds; round_off reconciles paise to
//                     the rounded total.
// Line items NEVER round under either mode — rounding at the line
// level causes over- and under-taxation and return mismatches.
export const TAX_ROUNDING_MODES = Object.freeze({
  PER_COMPONENT: "per_component",
  TOTAL_ONLY:    "total_only",
});
// Reducer default aligned with the server default (`total_only`) so
// that unconfigured outlets never diverge draft-vs-printed. The client
// hydrates the actual outlet setting on mount via SESSION_ROUNDING_SET.
export const tax_rounding_mode = TAX_ROUNDING_MODES.TOTAL_ONLY;

// Sprint 6 correction 4 · Service charge — outlet-level configuration
// designed to be tenant-configurable in production. Every knob defaults
// to the safest option: OFF. Tax treatment of service charge varies by
// jurisdiction and by outlet policy; the taxability flag exists so a
// deployment can flip it deliberately after legal review, not by
// accident.
export const OUTLET_CONFIG = Object.freeze({
  // Master switch. When false — the default — no service charge line
  // appears anywhere: order ticket, bill, reports.
  service_charge_enabled:   false,
  // Percentage applied to the F&B subtotal AFTER discounts and BEFORE
  // tax. Only read when `service_charge_enabled` is true.
  service_charge_pct:       10,
  // Whether the service charge amount enters the tax base. Confirm
  // per client BEFORE enabling. Under Indian practice this is usually
  // taxable at the same GST rate as the underlying category.
  service_charge_taxable:   false,
  // Whether service charge applies to liquor lines under VAT. Most
  // outlets do NOT charge service on alcohol. Default false; flip
  // only when the outlet explicitly wants it.
  service_charge_on_liquor: false,
});
